/**
 * @page /m/notifications
 * @description Mobile Portal — Benachrichtigungen.
 *   Liste aller Notifications, ungelesen hervorgehoben, Klick markiert als gelesen
 *   und navigiert optional zum Link. "Alle als gelesen" Button.
 * @api GET /api/portal/notifications
 * @api PUT /api/portal/notifications/:id/gelesen
 * @api PUT /api/portal/notifications/alle-gelesen
 * @roles portal_mitarbeiter
 * @i18n mobilePortal.notifications
 * @since v2.243.0
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Bell, CheckCheck, Info, AlertTriangle, Calendar, FileText, Clock, Inbox, ArrowLeft } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

/** Clean up raw Date.toString() patterns in notification text */
function sanitizeNachricht(text) {
  if (!text) return text;
  // Match raw JS Date.toString() like "Wed Jun 24 2026 00:00:00 GMT+0200 (Central European..."
  return text.replace(
    /\b(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun)\s+(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{1,2})\s+(\d{4})\s+\d{2}:\d{2}:\d{2}\s+GMT[^\)]*(?:\([^)]*\))?/g,
    (_, day, year, offset, match) => {
      try {
        const d = new Date(match || _);
        return d.toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' });
      } catch { return _; }
    }
  );
}

/** Icon based on notification typ */
function NotifIcon({ typ }) {
  const cls = 'w-5 h-5';
  switch (typ) {
    case 'urlaub': return <Calendar className={`${cls} text-green-500`} />;
    case 'warnung': return <AlertTriangle className={`${cls} text-amber-500`} />;
    case 'dokument': return <FileText className={`${cls} text-purple-500`} />;
    case 'zeit': return <Clock className={`${cls} text-blue-500`} />;
    case 'info': return <Info className={`${cls} text-[#338edc]`} />;
    default: return <Bell className={`${cls} text-[var(--text-tertiary,#6B7280)]`} />;
  }
}

/** Relative time display (e.g. "vor 3h", "vor 2 Tagen") */
function relativeTime(dateStr, t) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t('mobilePortal.notifications.justNow');
  if (mins < 60) return t('mobilePortal.notifications.minutesAgo', { n: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t('mobilePortal.notifications.hoursAgo', { n: hours });
  const days = Math.floor(hours / 24);
  if (days < 7) return t('mobilePortal.notifications.daysAgo', { n: days });
  return new Date(dateStr).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
}

export default function MobileNotifications() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [ungelesen, setUngelesen] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/portal/notifications`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
        setUngelesen(data.ungelesen || 0);
      }
    } catch (err) {
      console.error('MobileNotifications load error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markAsRead = async (id) => {
    try {
      await fetch(`${API}/api/portal/notifications/${id}/gelesen`, {
        method: 'PUT',
        credentials: 'include',
      });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, gelesen: true } : n));
      setUngelesen(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('markAsRead error:', err);
    }
  };

  const markAllRead = async () => {
    try {
      await fetch(`${API}/api/portal/notifications/alle-gelesen`, {
        method: 'PUT',
        credentials: 'include',
      });
      setNotifications(prev => prev.map(n => ({ ...n, gelesen: true })));
      setUngelesen(0);
    } catch (err) {
      console.error('markAllRead error:', err);
    }
  };

  const handleClick = async (notif) => {
    if (!notif.gelesen) {
      await markAsRead(notif.id);
    }
    if (notif.link) {
      navigate(notif.link);
    }
  };

  if (loading) {
    return (
      <div className="px-4 py-6">
        <div className="animate-pulse space-y-3">
          <div className="h-6 bg-[var(--bg-inset,#F8F9FC)] rounded w-1/2" />
          {[1, 2, 3].map(i => (
            <div key={i} className="h-16 bg-[var(--bg-inset,#F8F9FC)] rounded-2xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 pb-24">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate('/portal')}
            aria-label={t('mobilePortal.notifications.back', 'Zurück')}
            className="w-9 h-9 -ml-2 flex items-center justify-center rounded-xl text-[var(--text-secondary,#4B5563)] active:bg-[var(--bg-inset,#F8F9FC)] transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-lg font-bold text-[var(--text-primary,#1A1A2E)]">
            {t('mobilePortal.notifications.title')}
          </h1>
          {ungelesen > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-[#338edc] text-white text-[10px] font-bold">
              {ungelesen}
            </span>
          )}
        </div>
        {ungelesen > 0 && (
          <button
            onClick={markAllRead}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold text-[#338edc] bg-[#338edc]/5 active:bg-[#338edc]/10 transition-colors"
          >
            <CheckCheck className="w-3.5 h-3.5" />
            {t('mobilePortal.notifications.markAllRead')}
          </button>
        )}
      </div>

      {/* Notification List */}
      {notifications.length === 0 ? (
        <div className="text-center py-16">
          <Inbox className="w-12 h-12 text-[var(--text-tertiary,#6B7280)] mx-auto" />
          <p className="text-sm text-[var(--text-secondary,#4B5563)] mt-3">
            {t('mobilePortal.notifications.empty')}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {notifications.map(notif => (
            <button
              key={notif.id}
              onClick={() => handleClick(notif)}
              className={`w-full text-left flex items-start gap-3 px-4 py-3 rounded-2xl border transition-colors ${
                notif.gelesen
                  ? 'bg-[var(--bg-card,#fff)] border-[var(--border-card,#E5E7EB)]'
                  : 'bg-[#338edc]/5 border-[#338edc]/20'
              } active:bg-[var(--bg-inset,#F8F9FC)]`}
            >
              <div className="flex-shrink-0 mt-0.5">
                <NotifIcon typ={notif.typ} />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm ${notif.gelesen ? 'text-[var(--text-primary,#1A1A2E)]' : 'font-semibold text-[var(--text-primary,#1A1A2E)]'}`}>
                  {notif.titel}
                </p>
                {notif.nachricht && (
                  <p className="text-xs text-[var(--text-tertiary,#6B7280)] mt-0.5 line-clamp-2">
                    {sanitizeNachricht(notif.nachricht)}
                  </p>
                )}
                <p className="text-[10px] text-[var(--text-tertiary,#6B7280)] mt-1">
                  {relativeTime(notif.created_at, t)}
                </p>
              </div>
              {!notif.gelesen && (
                <div className="flex-shrink-0 mt-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#338edc]" />
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
